import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../../components/ui/Badge';
import {
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  Select,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { deletePmsDefect } from '../../../services/mutations';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import type { PmsDefect } from './useData';

type BoardTab = 'open' | 'closed' | 'all';
type LookupRow = { id: number; code?: string | null; name?: string | null };

const CLOSED_STATUS_CODES = new Set(['COMPLETED', 'VERIFIED', 'CLOSED', 'CANCELLED']);

const statusVariant: Record<string, 'default' | 'info' | 'success' | 'danger' | 'warning'> = {
  OPEN: 'warning',
  UNDER_REVIEW: 'info',
  ACTION_PLANNED: 'info',
  IN_PROGRESS: 'warning',
  AWAITING_PARTS: 'info',
  TEMPORARILY_REPAIRED: 'warning',
  COMPLETED: 'success',
  VERIFIED: 'success',
  CLOSED: 'default',
  CANCELLED: 'default',
};

const severityVariant: Record<string, 'default' | 'info' | 'success' | 'danger' | 'warning'> = {
  CRITICAL: 'danger',
  MAJOR: 'warning',
  MINOR: 'info',
};

const viewTabs: Array<{
  id: BoardTab;
  label: string;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'open',
    label: 'Open',
    icon: <ClockIcon className="h-4 w-4" />,
    emptyTitle: 'No open defects',
    emptyHint: 'Open defects awaiting action appear here.',
  },
  {
    id: 'closed',
    label: 'Closed',
    icon: <FolderIcon className="h-4 w-4" />,
    emptyTitle: 'No closed defects',
    emptyHint: 'Completed, verified, and closed defects appear here.',
  },
  {
    id: 'all',
    label: 'All',
    icon: <DocumentTextIcon className="h-4 w-4" />,
    emptyTitle: 'No defects yet',
    emptyHint: 'Record a defect when equipment issues are found on board.',
  },
];

function listFrom<T>(rows: T[] | { data?: T[] } | undefined): T[] {
  return Array.isArray(rows) ? rows : rows?.data ?? [];
}

function vesselLabel(row: PmsDefect) {
  const v = row.vessel as { vesselName?: string; name?: string } | null | undefined;
  return v?.vesselName ?? v?.name ?? (row.vesselId != null ? `#${row.vesselId}` : '');
}

function equipmentLabel(row: PmsDefect) {
  const eq = row.equipment as { equipmentCode?: string; name?: string; code?: string } | null | undefined;
  if (!eq) return '';
  const code = eq.equipmentCode ?? eq.code;
  if (code && eq.name) return `${code} · ${eq.name}`;
  return eq.name ?? code ?? (row.equipmentId != null ? `#${row.equipmentId}` : '');
}

function statusCode(row: PmsDefect) {
  return String((row.status as { code?: string } | null)?.code ?? '').toUpperCase();
}

function statusLabel(row: PmsDefect) {
  const s = row.status as { name?: string; code?: string } | null | undefined;
  return s?.name ?? s?.code ?? '';
}

function severityCode(row: PmsDefect) {
  return String((row.severity as { code?: string } | null)?.code ?? '').toUpperCase();
}

function severityLabel(row: PmsDefect) {
  const s = row.severity as { name?: string; code?: string } | null | undefined;
  return s?.name ?? s?.code ?? '';
}

function isClosed(row: PmsDefect) {
  return CLOSED_STATUS_CODES.has(statusCode(row));
}

function formatCreated(value?: string) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function sortValue(row: PmsDefect, key: string): string | number {
  switch (key) {
    case 'defectNumber':
      return String(row.defectNumber ?? '').toLowerCase();
    case 'title':
      return String(row.title ?? '').toLowerCase();
    case 'vessel':
      return vesselLabel(row).toLowerCase();
    case 'equipment':
      return equipmentLabel(row).toLowerCase();
    case 'severity':
      return severityLabel(row).toLowerCase();
    case 'status':
      return statusLabel(row).toLowerCase();
    case 'createdAt':
      return row.createdAt ? String(row.createdAt) : '';
    default:
      return '';
  }
}

const List: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };
  const confirm = useConfirm();

  const [tab, setTab] = useState<BoardTab>('open');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [vesselId, setVesselId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [severityId, setSeverityId] = useState('');

  type VesselOptionRow = { id: number; vesselName: string; isActive?: boolean };
  const { data: vesselsRaw, isLoading: vesselsLoading } = useSWR<
    VesselOptionRow[] | { data?: VesselOptionRow[] }
  >('/vessels?page=1&limit=500', fetcher, { revalidateOnFocus: false });
  const { data: statusesRaw } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-defect-statuses?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: severitiesRaw } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-defect-severities?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselOptions = useMemo(() => {
    const list = listFrom(vesselsRaw).filter((v) => v.isActive !== false);
    return list.map((v) => ({ value: String(v.id), label: v.vesselName }));
  }, [vesselsRaw]);

  const statusOptions = useMemo(() => {
    const list = listFrom(statusesRaw);
    return list.map((s) => ({ value: String(s.id), label: s.name ?? s.code ?? `#${s.id}` }));
  }, [statusesRaw]);

  const severityOptions = useMemo(() => {
    const list = listFrom(severitiesRaw);
    return list.map((s) => ({ value: String(s.id), label: s.name ?? s.code ?? `#${s.id}` }));
  }, [severitiesRaw]);

  const listUrl = useMemo(() => {
    const params = new URLSearchParams({ page: '1', limit: '500' });
    if (search.trim()) params.set('searchParams', search.trim());
    return `/pms-defects?${params.toString()}`;
  }, [search]);

  const { data, error, isLoading, mutate } = useSWR(listUrl, fetcher, {
    revalidateOnFocus: true,
  });

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'createdAt', direction: 'desc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'defectNumber' ||
        key === 'title' ||
        key === 'vessel' ||
        key === 'equipment' ||
        key === 'severity' ||
        key === 'status'
          ? 'asc'
          : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [search, vesselId, statusId, severityId, tab, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const baseRows = useMemo(() => {
    const normalized = normalizePaginatedListResponse<PmsDefect>(data, 1, 500);
    return normalized.data.filter((row) => {
      if (vesselId && String(row.vesselId) !== vesselId) return false;
      if (statusId && String(row.statusId) !== statusId) return false;
      if (severityId && String(row.severityId) !== severityId) return false;
      return true;
    });
  }, [data, vesselId, statusId, severityId]);

  const tabRows = useMemo(() => {
    if (tab === 'all') return baseRows;
    return baseRows.filter((row) => (tab === 'closed' ? isClosed(row) : !isClosed(row)));
  }, [baseRows, tab]);

  const openCount = useMemo(() => baseRows.filter((row) => !isClosed(row)).length, [baseRows]);
  const closedCount = useMemo(() => baseRows.filter((row) => isClosed(row)).length, [baseRows]);

  const sortedRows = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...tabRows].sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return av < bv ? -dir : dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [tabRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const hasFilters = Boolean(search.trim() || vesselId || statusId || severityId);

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setVesselId('');
    setStatusId('');
    setSeverityId('');
  };

  const switchTab = (next: BoardTab) => {
    if (next === tab) return;
    setTab(next);
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === tab) ?? viewTabs[0], [tab]);

  const tabCounts: Record<BoardTab, number | undefined> = {
    open: openCount,
    closed: closedCount,
    all: baseRows.length,
  };

  const handleDelete = async (e: React.MouseEvent, row: PmsDefect) => {
    e.stopPropagation();
    const label =
      row.defectNumber != null ? String(row.defectNumber) : `Defect #${row.id}`;
    const ok = await confirm({
      title: 'Delete defect',
      message: `Delete “${label}”?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deletePmsDefect(row.id);
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      window.alert(msg || (err instanceof Error ? err.message : 'Failed to delete defect'));
    }
  };

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label, icon }) => ({
        id,
        label,
        icon,
        count: tabCounts[id],
      }))}
      activeTab={tab}
      onTabChange={(value) => switchTab(value as BoardTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search defects…',
        title: 'Searches defect number and title',
      }}
      actions={
        <Link
          to="/pms/pms-defects/new"
          state={returnState}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
            colors.primary,
          )}
        >
          <PlusIcon className="h-4 w-4" />
          New defect
        </Link>
      }
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[150px]">
            <Select
              value={vesselId}
              options={vesselOptions}
              placeholder={vesselsLoading ? 'Loading vessels…' : 'Vessel'}
              showClearButton
              searchPlaceholder="Search vessels…"
              onChange={(e) => setVesselId(String(e.target.value))}
            />
          </div>
          <div className="w-[140px]">
            <Select
              value={severityId}
              options={severityOptions}
              placeholder="Severity"
              showClearButton
              onChange={(e) => setSeverityId(String(e.target.value))}
            />
          </div>
          <div className="w-[140px]">
            <Select
              value={statusId}
              options={statusOptions}
              placeholder="Status"
              showClearButton
              onChange={(e) => setStatusId(String(e.target.value))}
            />
          </div>
        </div>
      }
      filtersInline
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading defects…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon:
          tab === 'open' && !hasFilters ? (
            <DocumentTextIcon className="h-12 w-12" />
          ) : (
            <FolderIcon className="h-12 w-12" />
          ),
        title: hasFilters ? 'No matching defects' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : (
          <Link
            to="/pms/pms-defects/new"
            state={returnState}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New defect
          </Link>
        ),
      }}
      banner={
        error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            Could not load defects.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className="w-[5.5rem] px-3 py-3 font-medium" />
            <GeneralListSortableTh sortKey="defectNumber" className="w-[24%]">
              Defect
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vessel" className="hidden w-[14%] md:table-cell">
              Vessel
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="equipment" className="hidden w-[18%] lg:table-cell">
              Equipment
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="severity" className="hidden w-[12%] sm:table-cell">
              Severity
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="status" className="w-[12%]">
              Status
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="createdAt" className="hidden w-[12%] lg:table-cell">
              Created
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const code = statusCode(row);
            const sevCode = severityCode(row);
            const number =
              row.defectNumber != null ? String(row.defectNumber) : `DEF #${row.id}`;
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/pms/pms-defects/${row.id}`, { state: returnState })}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                  colors.border,
                )}
              >
                <td className="px-3 py-3.5">
                  <div className="flex flex-nowrap items-center">
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={`/pms/pms-defects/${row.id}`}
                      state={returnState}
                      title="View"
                      aria-label="View defect"
                      className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={`/pms/pms-defects/${row.id}/edit`}
                      state={returnState}
                      title="Edit"
                      aria-label="Edit defect"
                      className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      type="button"
                      title="Delete"
                      aria-label="Delete defect"
                      onClick={(e) => handleDelete(e, row)}
                      className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
                <td className="max-w-0 px-3 py-3.5">
                  <div className="min-w-0">
                    <p className={cn('truncate font-medium', colors.text)}>{number}</p>
                    <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                      {row.title != null ? String(row.title) : ''}
                    </p>
                    <p className={cn('mt-1 truncate text-xs md:hidden', colors.textMuted)}>
                      {[vesselLabel(row), equipmentLabel(row), severityLabel(row)]
                        .filter((v) => Boolean(v))
                        .join(' · ') || ''}
                    </p>
                  </div>
                </td>
                <td
                  className={cn('hidden max-w-0 truncate px-3 py-3.5 md:table-cell', colors.textMuted)}
                  title={vesselLabel(row)}
                >
                  {vesselLabel(row)}
                </td>
                <td
                  className={cn('hidden max-w-0 truncate px-3 py-3.5 lg:table-cell', colors.textMuted)}
                  title={equipmentLabel(row)}
                >
                  {equipmentLabel(row)}
                </td>
                <td className="hidden whitespace-nowrap px-3 py-3.5 sm:table-cell">
                  {Boolean(severityLabel(row)) ? (
                    <Badge variant={severityVariant[sevCode] ?? 'default'} className="whitespace-nowrap">
                      {severityLabel(row)}
                    </Badge>
                  ) : (
                    <span className="text-slate-400"></span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  <Badge variant={statusVariant[code] ?? 'default'} className="whitespace-nowrap">
                    {statusLabel(row)}
                  </Badge>
                </td>
                <td className={cn('hidden whitespace-nowrap px-3 py-3.5 lg:table-cell', colors.textMuted)}>
                  {formatCreated(row.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default List;
